import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import usePermissions from "datasets/hooks/usePermissions";
import useDatasetReportFQNs from "datasets/pages/dashboards/hooks/useDatasetReportFQNs";
import useDatasetReports from "datasets/pages/dashboards/hooks/useDatasetReports";
import useReport from "datasets/pages/dashboards/hooks/useReport";
import Loading from "~/components/Loading";
import SigmaEmbed from "~/components/SigmaEmbed";
import { Badge } from "~/components/ui/badge";
import useErrorToast from "~/contexts/error-toast";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const DatasetReport = () => {
  const { datasetId, reportId } = useParams();
  const { data: report } = useReport();
  const { mutate: mutateDataset } = useDataset();
  const { mutate: mutateReports } = useDatasetReports();
  const { showError } = useErrorToast();
  const { data: reportFQNs, mutate: mutateReportFQNs } = useDatasetReportFQNs();
  const navigate = useNavigate();
  const hasEditPermission = usePermissions("edit:catalog-report");

  const handleClose = () => navigate("..", { relative: "path" });

  const handleRemove = async () => {
    if (report && reportFQNs) {
      const filteredReports = reportFQNs?.filter((fqn) => fqn !== report.fullyQualifiedName);

      try {
        const resp = await fetch(`${VITE_CATALOG_API}/datasets/${datasetId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reports: filteredReports,
          }),
        });

        if (!resp.ok) {
          throw Error("There was a problem trying to remove the report");
        }

        await mutateDataset();
        await mutateReports();
        await mutateReportFQNs();
        handleClose();
      } catch (e: any) {
        console.error(e);
        rumClient?.recordError(e);
        showError(e.message);
      }
    }
  };

  const messageHandler = async (e: MessageEvent) => {
    if ("workbook:ondelete" === e.data.type) {
      await handleRemove();
    }
  };

  useEffect(() => {
    trackEvent("dataset_dashboard_viewed", {
      dataset_id: datasetId,
      dashboard_id: reportId,
    });
  }, [datasetId, reportId]);

  if (!report) {
    return <Loading />;
  }

  return (
    <SigmaEmbed
      title={
        <>
          {report.displayName}
          <Badge variant="primary">BETA</Badge>
        </>
      }
      datasetId={datasetId}
      reportFqn={report.fullyQualifiedName}
      messageHandler={messageHandler}
      onClose={handleClose}
      allowEdits={hasEditPermission}
    />
  );
};

export default DatasetReport;
